import { ArrowRight } from "lucide-react";

import type {
  DashboardRateWatchHistoryPoint,
  DashboardSnapshot,
} from "../../../lib/dashboard";
import { trimTrailingZeros } from "../../../lib/dashboard";
import { formatCurrency } from "../../../lib/financials";
import { Button } from "../../ui/button";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRatePercent(value: number): string {
  return trimTrailingZeros((value * 100).toFixed(3)) + "%";
}

function formatSignedPercent(value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "\u2212" : "";
  const abs = Math.abs(value);
  return sign + trimTrailingZeros((abs * 100).toFixed(3)) + "%";
}

type SparklinePoint = {
  dateLabel: string;
  x: number;
  y: number;
};

type SparklineModel = {
  areaPath: string;
  first: SparklinePoint;
  last: SparklinePoint;
  linePath: string;
};

function buildSparklineModel(
  history: DashboardRateWatchHistoryPoint[],
): SparklineModel | null {
  const valid = history.filter(
    (p) => typeof p.baseRatePercent === "number",
  ) as (DashboardRateWatchHistoryPoint & { baseRatePercent: number })[];

  if (valid.length < 2) {
    return null;
  }

  const width = 280;
  const height = 96;
  const paddingX = 8;
  const paddingTop = 14;
  const paddingBottom = 22;
  const usableW = width - paddingX * 2;
  const usableH = height - paddingTop - paddingBottom;

  const rates = valid.map((p) => p.baseRatePercent);
  const minRate = Math.min(...rates);
  const maxRate = Math.max(...rates);
  const range = maxRate - minRate;

  const points = valid.map((p, i) => {
    const x = paddingX + (usableW / (valid.length - 1)) * i;
    const y =
      range === 0
        ? paddingTop + usableH / 2
        : paddingTop + ((maxRate - p.baseRatePercent) / range) * usableH;
    return {
      dateLabel: p.dateLabel,
      x,
      y,
    };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const baseline = height - paddingBottom;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const areaPath = `${linePath} L ${last.x.toFixed(
    1,
  )} ${baseline} L ${first.x.toFixed(1)} ${baseline} Z`;

  return {
    areaPath,
    first,
    last,
    linePath,
  };
}

function formatPoints(value: number | null): string {
  if (typeof value !== "number") return "—";
  return `${trimTrailingZeros((value * 100).toFixed(3))} pts`;
}

function formatMonthlyPayment(value: number | null): string {
  if (typeof value !== "number") return "—";
  return `${formatCurrency(value)}/mo`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RateWatchPreview({
  onOpenRateWatch,
  snapshot,
}: {
  onOpenRateWatch: () => void;
  snapshot: DashboardSnapshot;
}) {
  const { rateWatch } = snapshot;
  const baseRate = rateWatch.baseRatePercent;
  const hasRate = typeof baseRate === "number";
  const delta = rateWatch.weeklyDeltaPercent;

  const pillClass =
    delta !== null && delta < 0
      ? "pill-success"
      : delta !== null && delta > 0
        ? "pill-warning"
        : "pill-neutral";

  const sparkline = hasRate ? buildSparklineModel(rateWatch.history) : null;
  const asOfLabel = rateWatch.baseRateAsOfLabel
    ? `As of ${rateWatch.baseRateAsOfLabel}`
    : "Latest available rate";

  return (
    <section className="surface-panel flex h-full flex-col bg-card p-5 sm:p-[22px]">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="eyebrow-label">Rate watch</p>
          <h2 className="text-[1.06rem] font-[620] tracking-[-0.01em] text-foreground">
            Market rate and your pricing
          </h2>
        </div>
        <Button
          aria-label="View rate watch"
          className="shrink-0 whitespace-nowrap"
          onClick={onOpenRateWatch}
          size="sm"
          variant="outline"
        >
          View rate watch
          <ArrowRight className="ml-1 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        </Button>
      </div>

      {hasRate ? (
        <div className="mt-4 flex flex-1 flex-col gap-3">
          <div className="grid gap-3 rounded-2xl bg-muted/45 p-3.5 sm:grid-cols-[minmax(8.5rem,0.36fr)_minmax(0,1fr)] sm:p-4">
            <div className="flex min-w-0 flex-col justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Average market
                </p>
                <p className="display-figure mt-2 text-[2.35rem] leading-none tracking-[-0.025em] text-foreground">
                  {formatRatePercent(baseRate)}
                </p>
              </div>
              {delta !== null ? (
                <span className={pillClass}>
                  {formatSignedPercent(delta)} this week
                </span>
              ) : (
                <span className="pill-neutral">No weekly move</span>
              )}
              <p className="text-xs leading-5 text-muted-foreground">
                {asOfLabel}
              </p>
            </div>

            {sparkline ? (
              <div className="min-w-0 rounded-xl bg-card/82 px-3 py-2.5 ring-1 ring-border/60">
                <div className="flex items-center justify-between gap-3 text-[11px] font-medium text-muted-foreground">
                  <span>{sparkline.first.dateLabel}</span>
                  <span>{sparkline.last.dateLabel}</span>
                </div>
                <svg
                  aria-label="Rate trend sparkline"
                  viewBox="0 0 280 96"
                  className="mt-1 block h-[7.25rem] w-full"
                >
                  <line
                    x1="8"
                    x2="272"
                    y1="74"
                    y2="74"
                    stroke="var(--border)"
                    strokeWidth="1"
                  />
                  <path
                    d={sparkline.areaPath}
                    fill="var(--primary-soft)"
                    opacity="0.5"
                  />
                  <path
                    d={sparkline.linePath}
                    stroke="var(--primary)"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {[sparkline.first, sparkline.last].map((point) => (
                    <circle
                      key={`${point.dateLabel}-${point.x}`}
                      cx={point.x}
                      cy={point.y}
                      r="3"
                      fill="var(--card)"
                      stroke="var(--primary)"
                      strokeWidth="2"
                    />
                  ))}
                </svg>
              </div>
            ) : (
              <div className="flex min-h-[9.4rem] items-center justify-center rounded-xl bg-card/82 px-4 text-center text-sm text-muted-foreground ring-1 ring-border/60">
                Trend appears after two rate checks.
              </div>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            <RateWatchFact
              label="Monthly scenario"
              value={formatMonthlyPayment(rateWatch.scenarioPayment)}
              detail="Watched target payment estimate."
            />
            <RateWatchFact
              label="Pricing adj."
              value={formatPoints(rateWatch.llpaPoints)}
              detail="Model adjustment, not lender quote."
            />
            <RateWatchFact
              label="Rate checks"
              value={`${rateWatch.history.length}`}
              detail="Market observations in this window."
            />
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          Average rate will appear once Sill has fresh rate-watch data for your
          profile.
        </p>
      )}
    </section>
  );
}

function RateWatchFact({
  detail,
  label,
  value,
}: {
  detail: string;
  label: string;
  value: string;
}) {
  return (
    <div className="min-w-0 rounded-xl bg-background/72 px-3.5 py-3 ring-1 ring-border/70">
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 truncate text-[1rem] font-bold tabular-nums leading-6 text-foreground">
        {value}
      </p>
      <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
        {detail}
      </p>
    </div>
  );
}
